import { useCallback, useEffect, useId, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import StatusPill from "@/components/common/StatusPill";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Trash2, Plus, ExternalLink, Quote, Pencil } from "lucide-react";
import { eur } from "@/lib/formato";
import { leggiImporto, messaggioErrore } from "@/lib/preventivi";
import { registraSalvataggio } from "@/lib/salvataggi";
import { useConferma } from "@/hooks/use-conferma";


const BADGE_STATO = {
  omaggio: { stato: "ok", label: "Omaggio" },
  regalo:  { stato: "info", label: "Regalo" },
  promo:   { stato: "attenzione", label: "Promo" },
  incluso: { stato: "info", label: "Incluso" },
};

const TIPI_FORNITURA = [
  { v: "cucina",  l: "Cucina" },
  { v: "divano",  l: "Divano" },
  { v: "camera",  l: "Camera" },
  { v: "armadio", l: "Armadio" },
  { v: "cabina",  l: "Cabina armadio" },
  { v: "altro",   l: "Altro" },
];

/* ================================================================= */
/* TIPO FORNITURA — selettore visibile nel blocco Scheda              */
/* ================================================================= */
export function TipoFornituraSelector({ preventivoId, tipoAttuale, readOnly, onChanged }) {
  const { chiedi, dialogoConferma } = useConferma();
  const cambia = async (v) => {
    if (v === tipoAttuale) return;
    const ok = await chiedi({
      titolo: "Cambiare il tipo di fornitura?",
      descrizione:
        "Roadmap e omaggi vengono rigenerati per il nuovo tipo: le modifiche fatte a mano su quei due blocchi si perdono.",
      verbo: "Cambia il tipo",
    });
    if (!ok) return;
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/tipo-fornitura`, { tipo_fornitura: v }));
      onChanged && onChanged();
      toast.success("Tipo fornitura aggiornato");
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  };
  return (
    <div className="space-y-inside">
      {dialogoConferma}
      <Label className="text-xs text-muted-foreground">Tipo di fornitura</Label>
      <Select value={tipoAttuale || "cucina"} onValueChange={cambia} disabled={readOnly}>
        <SelectTrigger data-testid="tipo-fornitura-select"><SelectValue /></SelectTrigger>
        <SelectContent>
          {TIPI_FORNITURA.map((t) => (
            <SelectItem key={t.v} value={t.v} data-testid={`tipo-${t.v}`}>{t.l}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        Determina i passi della roadmap, gli omaggi proposti e la disponibilità del box blocca-prezzo.
      </p>
    </div>
  );
}

/* ================================================================= */
/* BLOCCO 7 — Punti distintivi                                        */
/* ================================================================= */
const OP_LABEL = {
  eq: "uguale a",
  ne: "diverso da",
  gt: "maggiore di",
  gte: "almeno",
  lt: "minore di",
  lte: "al massimo",
  in: "tra",
  not_in: "fuori da",
  exists: "presente",
  contains: "che contiene",
};

function formatoDettaglio(d) {
  const trovatoTxt =
    d.trovato === null || d.trovato === undefined
      ? "non è nella distinta"
      : `nella distinta è ${Array.isArray(d.trovato) ? d.trovato.join(", ") : d.trovato}`;
  if (d.op === "exists") return `${d.path}: ${trovatoTxt}`;
  const attesoTxt = Array.isArray(d.atteso) ? d.atteso.join(", ") : d.atteso;
  return `${d.path} deve essere ${OP_LABEL[d.op] || d.op} ${attesoTxt} — ${trovatoTxt}`;
}

// Il 422 di POST punti arriva in due forme: una stringa quando sono già 5
// (vedi backend/libreria_punti.py `MAX_PUNTI`), un oggetto {message, verifiche}
// quando la condizione sulla distinta non torna. `toast.error` su un oggetto
// stampa "[object Object]": qui si spiega davvero cosa non va, riga per riga.
function messaggioErroreAggiungiPunto(e) {
  const d = e.response?.data?.detail;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const ragioni = (d.verifiche || []).filter((v) => !v.ok).map(formatoDettaglio);
    return ragioni.length ? `${d.message} — ${ragioni.join(" · ")}` : d.message;
  }
  return messaggioErrore(e);
}

function flattenDistinta(obj, prefix = "") {
  const out = [];
  if (!obj || typeof obj !== "object") return out;
  for (const [k, v] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${k}` : k;
    if (v && typeof v === "object" && !Array.isArray(v)) {
      out.push(...flattenDistinta(v, path));
    } else {
      out.push({ path, valore: Array.isArray(v) ? v.join(", ") : String(v) });
    }
  }
  return out;
}

export function PuntiDistintiviBlock({ preventivoId, readOnly }) {
  const { chiedi, dialogoConferma } = useConferma();
  const [punti, setPunti] = useState([]);
  const [suggeriti, setSuggeriti] = useState(null); // { distinta, voci }
  const [busyAdd, setBusyAdd] = useState(null); // libreria_id in corso
  const [busyDel, setBusyDel] = useState(null); // punto id in corso
  const [openDistinta, setOpenDistinta] = useState(false);
  const [distintaTxt, setDistintaTxt] = useState("{}");
  const [distintaErr, setDistintaErr] = useState(null);
  const [busyDistinta, setBusyDistinta] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([
        api.get(`/preventivatore/${preventivoId}/punti`),
        api.get(`/preventivatore/${preventivoId}/punti/suggeriti`),
      ]);
      setPunti(p.data || []);
      setSuggeriti(s.data);
    } catch (e) {
      toast.error(messaggioErrore(e));
    }
  }, [preventivoId]);
  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (openDistinta && suggeriti) {
      setDistintaTxt(JSON.stringify(suggeriti.distinta || {}, null, 2));
      setDistintaErr(null);
    }
  }, [openDistinta, suggeriti]);

  const aggiungi = async (libreriaId) => {
    setBusyAdd(libreriaId);
    try {
      await registraSalvataggio(api.post(`/preventivatore/${preventivoId}/punti`, { libreria_id: libreriaId }));
      await load();
      toast.success("Punto aggiunto");
    } catch (e) {
      toast.error(messaggioErroreAggiungiPunto(e));
    } finally {
      setBusyAdd(null);
    }
  };

  const rimuovi = async (id) => {
    const ok = await chiedi({
      titolo: "Rimuovere questo punto distintivo?",
      descrizione:
        "Esce dal documento. Con uno o due punti il preventivo non si pubblica: o nessuno, o da 3 a 5.",
      verbo: "Rimuovi il punto",
    });
    if (!ok) return;
    setBusyDel(id);
    try {
      await registraSalvataggio(api.delete(`/preventivatore/${preventivoId}/punti/${id}`));
      await load();
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusyDel(null);
    }
  };

  const salvaDistinta = async () => {
    let parsed;
    try {
      parsed = JSON.parse(distintaTxt || "{}");
    } catch {
      setDistintaErr("Questo non è JSON valido: controlla parentesi e virgole.");
      return;
    }
    if (typeof parsed !== "object" || Array.isArray(parsed) || parsed === null) {
      setDistintaErr('La distinta deve essere un oggetto — es. { "top": { "spessore_cm": 2 } }.');
      return;
    }
    setDistintaErr(null);
    setBusyDistinta(true);
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/distinta`, { distinta: parsed }));
      await load();
      setOpenDistinta(false);
      toast.success("Distinta aggiornata — i punti si sono riverificati");
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally {
      setBusyDistinta(false);
    }
  };

  if (!suggeriti) return <p className="text-xs text-muted-foreground">Caricamento…</p>;

  const usati = new Set(punti.map((p) => p.libreria_id).filter(Boolean));
  const disponibili = (suggeriti.voci || []).filter((v) => !usati.has(v.libreria.id));
  const nDati = flattenDistinta(suggeriti.distinta).length;
  const count = punti.length;
  // Facoltativi (corsa del 24-09): nessuno, oppure da 3 a 5. Lo stesso
  // contratto di `valida_per_invio` in backend/documento_pubblico.py.
  const aPosto = count >= 3 && count <= 5;
  const statoContatore = count === 0 ? "neutro" : aPosto ? "ok" : "attenzione";
  const testoContatore =
    count === 0
      ? "Nessun punto · sono facoltativi"
      : aPosto
        ? `${count} di 3–5 · a posto`
        : `${count} di 3–5 · ne servono almeno 3, oppure nessuno`;

  return (
    <div className="space-y-4" data-testid="punti-distintivi-block">
      {dialogoConferma}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <StatusPill stato={statoContatore} dot={false} data-testid="punti-contatore">
          {testoContatore}
        </StatusPill>
        {!readOnly ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenDistinta(true)}
            data-testid="btn-modifica-distinta"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Distinta di questo preventivo ({nDati} {nDati === 1 ? "dato" : "dati"})
          </Button>
        ) : null}
      </div>

      {count === 0 ? (
        <p className="text-xs text-muted-foreground">
          Senza punti, il blocco non compare nel documento del cliente.
        </p>
      ) : (
        <ul className="space-y-2">
          {punti.map((p) => (
            <li
              key={p.id}
              className="rounded-md border bg-card p-group flex items-start gap-3"
              data-testid={`punto-scelto-${p.id}`}
            >
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">{p.titolo}</p>
                {p.cosa_si_vede ? (
                  <p className="text-xs text-muted-foreground mt-micro">{p.cosa_si_vede}</p>
                ) : null}
              </div>
              {!readOnly ? (
                <button
                  type="button"
                  onClick={() => rimuovi(p.id)}
                  disabled={busyDel === p.id}
                  className="text-muted-foreground hover:text-danger-foreground"
                  data-testid={`punto-rimuovi-${p.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {!readOnly ? (
        disponibili.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">
            {suggeriti.voci.length === 0
              ? "La libreria dei punti distintivi è vuota. Chiedi al titolare di popolarla dalle Impostazioni."
              : "Hai già scelto tutta la libreria disponibile."}
          </p>
        ) : (
          <div className="space-y-2">
            <p className="text-eyebrow uppercase text-muted-foreground">Libreria — non ancora scelti</p>
            <ul className="space-y-2">
              {disponibili.map((v) => {
                const lib = v.libreria;
                const bloccato = !v.condizione_soddisfatta;
                const ragioni = (v.dettagli || []).filter((d) => !d.ok).map(formatoDettaglio);
                return (
                  <li
                    key={lib.id}
                    className={`rounded-md border p-group ${bloccato ? "bg-muted" : "bg-card"}`}
                    data-testid={`libreria-voce-${lib.codice}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{lib.titolo}</p>
                        <p className="text-xs text-muted-foreground mt-micro">{lib.cosa_si_vede}</p>
                        {bloccato ? (
                          <p
                            className="text-xs text-warning-foreground mt-inside"
                            data-testid={`libreria-voce-motivo-${lib.codice}`}
                          >
                            Non ancora proponibile: {ragioni.join(" · ") || "condizione non soddisfatta"}
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={bloccato || busyAdd === lib.id || count >= 5}
                        onClick={() => aggiungi(lib.id)}
                        data-testid={`libreria-voce-aggiungi-${lib.codice}`}
                      >
                        <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )
      ) : null}

      <Dialog open={openDistinta} onOpenChange={setOpenDistinta}>
        <DialogContent className="sm:max-w-lg" data-testid="dialog-distinta">
          <DialogHeader>
            <DialogTitle>Distinta di questo preventivo</DialogTitle>
            <DialogDescription>
              È il dato contro cui si verificano le condizioni dei punti distintivi — per esempio lo
              spessore del top. Correggila qui se un punto risulta bloccato per errore.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={10}
            value={distintaTxt}
            onChange={(e) => setDistintaTxt(e.target.value)}
            className="font-mono text-xs"
            data-testid="distinta-textarea"
          />
          {distintaErr ? <p className="text-xs text-danger-foreground">{distintaErr}</p> : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDistinta(false)}>
              Annulla
            </Button>
            <Button onClick={salvaDistinta} disabled={busyDistinta} data-testid="distinta-salva">
              {busyDistinta ? "Salvataggio…" : "Salva distinta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ================================================================= */
/* BLOCCO 12 — Roadmap                                                */
/* ================================================================= */
export function RoadmapBlock({ preventivoId, readOnly }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/preventivatore/${preventivoId}/roadmap`);
      setData(r.data);
    } catch (e) { toast.error(messaggioErrore(e)); }
  }, [preventivoId]);
  useEffect(() => { load(); }, [load]);

  const rigenera = async () => {
    setBusy(true);
    try {
      await registraSalvataggio(api.post(`/preventivatore/${preventivoId}/roadmap/rigenera`, {}));
      await load();
      toast.success("Roadmap rigenerata dal catalogo");
    } catch (e) {
      toast.error(messaggioErrore(e));
    } finally { setBusy(false); }
  };

  const patchPasso = async (id, patch) => {
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/roadmap/passi/${id}`, patch));
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  if (!data) return <p className="text-xs text-muted-foreground">Caricamento…</p>;

  const rep = data.report;
  return (
    <div className="space-y-4" data-testid="roadmap-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-sm text-foreground">
            Dalla firma alla cucina montata,{" "}
            <span className="font-semibold text-primary">
              indicativamente {rep.settimane_min}–{rep.settimane_max} settimane
            </span>
            {" "}({rep.n_passi} passi · {rep.giorni_min}–{rep.giorni_max} giorni).
          </p>
        </div>
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={rigenera} disabled={busy} data-testid="roadmap-rigenera">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Rigenera dal catalogo
          </Button>
        ) : null}
      </div>

      <ol className="space-y-2">
        {data.passi.map((p, idx) => (
          <li
            key={p.id}
            className={`grid grid-cols-12 gap-3 items-start rounded-md border px-3 py-2.5 ${
              p.attivo ? " bg-card" : " bg-muted opacity-70"
            }`}
            data-testid={`roadmap-passo-${p.codice}`}
          >
            <div className="col-span-1 text-right">
              <span
                className="inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold"
                
              >
                {idx + 1}
              </span>
            </div>
            <div className="col-span-11 sm:col-span-6 space-y-1">
              <Input
                defaultValue={p.etichetta}
                disabled={readOnly}
                onBlur={(e) => e.target.value !== p.etichetta && patchPasso(p.id, { etichetta: e.target.value })}
                className="text-sm font-medium"
                data-testid={`roadmap-etichetta-${p.codice}`}
              />
              <Input
                defaultValue={p.attore}
                disabled={readOnly}
                onBlur={(e) => e.target.value !== p.attore && patchPasso(p.id, { attore: e.target.value })}
                className="text-xs text-muted-foreground"
                data-testid={`roadmap-attore-${p.codice}`}
              />
            </div>
            <div className="col-span-9 sm:col-span-3 flex items-center gap-2">
              <Input
                type="number"
                min={0}
                defaultValue={p.durata_giorni_min}
                disabled={readOnly}
                onBlur={(e) => Number(e.target.value) !== p.durata_giorni_min &&
                  patchPasso(p.id, { durata_giorni_min: Number(e.target.value) })}
                className="text-xs w-16 text-right"
                data-testid={`roadmap-min-${p.codice}`}
              />
              <span className="text-xs text-muted-foreground">–</span>
              <Input
                type="number"
                min={0}
                defaultValue={p.durata_giorni_max}
                disabled={readOnly}
                onBlur={(e) => Number(e.target.value) !== p.durata_giorni_max &&
                  patchPasso(p.id, { durata_giorni_max: Number(e.target.value) })}
                className="text-xs w-16 text-right"
                data-testid={`roadmap-max-${p.codice}`}
              />
              <span className="text-xs text-muted-foreground">gg</span>
            </div>
            <div className="col-span-3 sm:col-span-2 flex justify-end items-center gap-2">
              {!readOnly ? (
                <Switch
                  checked={p.attivo}
                  onCheckedChange={(v) => patchPasso(p.id, { attivo: v })}
                  data-testid={`roadmap-attivo-${p.codice}`}
                />
              ) : (
                <Badge variant="outline" className="text-xs">{p.attivo ? "Attivo" : "Nascosto"}</Badge>
              )}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ================================================================= */
/* BLOCCO 11 — Omaggi                                                 */
/* ================================================================= */
export function OmaggiBlock({ preventivoId, readOnly }) {
  const { chiedi, dialogoConferma } = useConferma();
  const [items, setItems] = useState(null);
  const [nuova, setNuova] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/preventivatore/${preventivoId}/omaggi`);
      setItems(r.data || []);
    } catch (e) { toast.error(messaggioErrore(e)); }
  }, [preventivoId]);
  useEffect(() => { load(); }, [load]);

  const patch = async (id, p) => {
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/omaggi/${id}`, p));
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  const aggiungi = async () => {
    const testo = nuova.trim();
    if (!testo || busy) return;
    setBusy(true);
    try {
      await registraSalvataggio(api.post(`/preventivatore/${preventivoId}/omaggi`, { etichetta: testo }));
      setNuova("");
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
    finally { setBusy(false); }
  };

  const togli = async (o) => {
    const ok = await chiedi({
      titolo: "Togliere questo omaggio?",
      descrizione: `«${o.etichetta}» esce dal documento del cliente. Se serve, lo si riscrive.`,
      verbo: "Togli l'omaggio",
    });
    if (!ok) return;
    try {
      await registraSalvataggio(api.delete(`/preventivatore/${preventivoId}/omaggi/${o.id}`));
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  if (items === null) return <p className="text-xs text-muted-foreground">Caricamento…</p>;

  return (
    <div className="space-y-group" data-testid="omaggi-block">
      {dialogoConferma}
      {items.length ? (
        <ul className="space-y-2">
          {items.map((o) => {
            const st = BADGE_STATO[o.badge] || BADGE_STATO.omaggio;
            const chiave = o.libera ? `libero-${o.id}` : o.codice;
            return (
              <li
                key={o.id}
                className={`flex items-start gap-3 rounded-md border px-3 py-2.5 ${
                  o.attivo ? " bg-card" : " bg-muted opacity-70"
                }`}
                data-testid={`omaggio-${chiave}`}
              >
                <StatusPill
                  stato={st.stato}
                  dot={false}
                  className="mt-0.5 uppercase"
                  data-testid={`omaggio-badge-${chiave}`}
                >
                  {st.label}
                </StatusPill>
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">{o.etichetta}</p>
                  {o.libera ? (
                    <p className="text-xs text-muted-foreground">Voce fuori catalogo</p>
                  ) : o.descrizione ? (
                    <p className="text-xs text-muted-foreground">{o.descrizione}</p>
                  ) : null}
                  {!o.libera ? (
                    <Textarea
                      rows={1}
                      defaultValue={o.nota || ""}
                      disabled={readOnly}
                      placeholder="Nota per il cliente (facoltativa)"
                      className="text-xs mt-1"
                      onBlur={(e) => (e.target.value || "") !== (o.nota || "") && patch(o.id, { nota: e.target.value })}
                      data-testid={`omaggio-nota-${chiave}`}
                    />
                  ) : null}
                </div>
                {readOnly ? (
                  <Badge variant="outline" className="text-xs">{o.attivo ? "Attivo" : "Nascosto"}</Badge>
                ) : o.libera ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => togli(o)}
                    aria-label={`Togli «${o.etichetta}»`}
                    data-testid={`omaggio-togli-${chiave}`}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                ) : (
                  <Switch
                    checked={o.attivo}
                    onCheckedChange={(v) => patch(o.id, { attivo: v })}
                    aria-label={`${o.etichetta}: nel documento`}
                    data-testid={`omaggio-attivo-${chiave}`}
                  />
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs text-muted-foreground">Nessun omaggio di casa per questo tipo di fornitura.</p>
      )}

      {!readOnly ? (
        <form
          className="flex flex-col gap-inside sm:flex-row"
          onSubmit={(e) => { e.preventDefault(); aggiungi(); }}
          data-testid="omaggio-libero-form"
        >
          <Label htmlFor={`omaggio-libero-${preventivoId}`} className="sr-only">
            Omaggio fuori catalogo
          </Label>
          <Input
            id={`omaggio-libero-${preventivoId}`}
            value={nuova}
            maxLength={160}
            onChange={(e) => setNuova(e.target.value)}
            placeholder="Un omaggio fuori catalogo, es. Lavello in fragranite"
            data-testid="omaggio-libero-input"
          />
          <Button
            type="submit"
            variant="outline"
            disabled={busy || !nuova.trim()}
            className="shrink-0"
            data-testid="omaggio-libero-aggiungi"
          >
            <Plus className="h-4 w-4 mr-1" aria-hidden /> Aggiungi
          </Button>
        </form>
      ) : null}
    </div>
  );
}

/* ================================================================= */
/* BLOCCO 13 — Pagamenti                                              */
/* ================================================================= */
export function PagamentiBlock({ preventivoId, tipoFornitura, totaleIndicativo, readOnly }) {
  const { chiedi, dialogoConferma } = useConferma();
  const [data, setData] = useState(null);
  const [tabellaImporti, setTabellaImporti] = useState([]);
  const [openNewFin, setOpenNewFin] = useState(false);
  const [openNewBonifico, setOpenNewBonifico] = useState(false);
  const [openBlocca, setOpenBlocca] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/preventivatore/${preventivoId}/pagamenti`);
      setData(r.data);
      const t = await api.get(`/impostazioni/finanziamento/tabella`);
      setTabellaImporti(t.data || []);
    } catch (e) { toast.error(messaggioErrore(e)); }
  }, [preventivoId]);
  useEffect(() => { load(); }, [load]);

  const setSaldo = async (v) => {
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/saldo-modalita`, { saldo_modalita: v }));
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  const patchModulo = async (id, p) => {
    try {
      await registraSalvataggio(api.patch(`/preventivatore/${preventivoId}/pagamenti/moduli/${id}`, p));
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };
  const delModulo = async (id) => {
    const ok = await chiedi({
      titolo: "Rimuovere questo modulo di pagamento?",
      descrizione:
        "Esce dal blocco pagamenti del documento: il cliente non lo vedrà più fra le modalità proposte.",
      verbo: "Rimuovi il modulo",
    });
    if (!ok) return;
    try {
      await registraSalvataggio(api.delete(`/preventivatore/${preventivoId}/pagamenti/moduli/${id}`));
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
  };
  const creaModulo = async (payload) => {
    try {
      await registraSalvataggio(api.post(`/preventivatore/${preventivoId}/pagamenti/moduli`, payload));
      await load();
      toast.success("Modulo di pagamento aggiunto");
    } catch (e) { toast.error(messaggioErrore(e)); }
  };

  if (!data) return <p className="text-xs text-muted-foreground">Caricamento…</p>;

  const moduliBonifico    = data.moduli.filter((m) => m.tipo === "bonifico");
  const moduliFin         = data.moduli.filter((m) => m.tipo === "finanziamento");
  const moduliBlocca      = data.moduli.filter((m) => m.tipo === "blocca_prezzo");
  const finConsigliato    = totaleIndicativo != null && Number(totaleIndicativo) > 5000;
  const bloccaConsentito  = tipoFornitura === "cucina";

  return (
    <div className="space-y-5" data-testid="pagamenti-block">
      {dialogoConferma}
      {/* Modalità di saldo — scelta a livello di contratto */}
      <div className="rounded-md border p-3 space-y-2">
        <Label className="text-xs text-muted-foreground">Modalità di saldo (scelta del contratto)</Label>
        <div className="grid grid-cols-2 gap-2">
          {[
            { v: "prima_consegna",  l: "Saldo prima della consegna" },
            { v: "dopo_montaggio",  l: "Saldo dopo il montaggio" },
          ].map((o) => {
            const active = data.saldo_modalita === o.v;
            return (
              <button
                key={o.v}
                type="button"
                onClick={() => !readOnly && setSaldo(o.v)}
                disabled={readOnly}
                className={[
                  "rounded-md border px-3 py-2 text-xs font-semibold",
                  active
                    ? "border-transparent bg-brand-subtle text-brand-subtle-foreground"
                    : "bg-card text-foreground hover:bg-accent",
                ].join(" ")}
                data-testid={`saldo-${o.v}`}
              >
                {o.l}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-muted-foreground">
          Entrambe le formule sono ammesse: la scelta si fa sul singolo contratto.
        </p>
      </div>

      {/* Bonifico */}
      <ModuloSezione
        titolo="Bonifico"
        descrizione="Acconto alla firma, saldo secondo la modalità scelta sul contratto. Configurabile in 2 o 3 tempi."
      >
        {moduliBonifico.map((m) => (
          <BonificoRiga
            key={m.id}
            modulo={m}
            readOnly={readOnly}
            onPatch={(p) => patchModulo(m.id, p)}
            onDelete={() => delModulo(m.id)}
          />
        ))}
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={() => setOpenNewBonifico(true)} data-testid="btn-new-bonifico">
            <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi bonifico
          </Button>
        ) : null}
      </ModuloSezione>

      {/* Finanziamento */}
      <ModuloSezione
        titolo={
          <span className="flex items-center gap-2">
            Finanziamento
            {finConsigliato && !moduliFin.length ? (
              <StatusPill stato="attenzione" dot={false}>
                consigliato sopra 5.000€
              </StatusPill>
            ) : null}
          </span>
        }
        descrizione="Rate lette dalla tabella caricata in impostazioni. Nessuna rata si calcola: se una durata non è nella tabella, non si può proporre."
      >
        {moduliFin.map((m) => (
          <FinanziamentoRiga key={m.id} modulo={m} readOnly={readOnly} onDelete={() => delModulo(m.id)} />
        ))}
        {!readOnly ? (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setOpenNewFin(true)}
            disabled={tabellaImporti.length === 0}
            data-testid="btn-new-finanziamento"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi finanziamento
          </Button>
        ) : null}
        {tabellaImporti.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            La tabella rate è vuota. Caricala dalle Impostazioni prima di proporre il finanziamento.
          </p>
        ) : null}
      </ModuloSezione>

      {/* Blocca prezzo — solo cucina */}
      {bloccaConsentito ? (
        <ModuloSezione
          titolo="Blocca-prezzo"
          descrizione="Un acconto che tiene fermo il prezzo per qualche giorno, in percentuale o in euro. Solo sulle cucine."
        >
          {moduliBlocca.map((m) => (
            <BloccaPrezzoRiga
              key={m.id}
              modulo={m}
              readOnly={readOnly}
              totaleIndicativo={totaleIndicativo}
              onPatch={(p) => patchModulo(m.id, p)}
              onDelete={() => delModulo(m.id)}
            />
          ))}
          {!readOnly ? (
            <Button size="sm" variant="outline" onClick={() => setOpenBlocca(true)} data-testid="btn-new-blocca-prezzo">
              <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi blocca-prezzo
            </Button>
          ) : null}
        </ModuloSezione>
      ) : (
        <div className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
          Il box blocca-prezzo è disponibile solo su preventivi di tipo <strong>cucina</strong>.
        </div>
      )}

      {/* Dialog: nuovo bonifico */}
      {openNewBonifico ? (
        <BonificoDialog
          onClose={() => setOpenNewBonifico(false)}
          onSubmit={async (payload) => {
            await creaModulo({ tipo: "bonifico", contenuto: payload });
            setOpenNewBonifico(false);
          }}
        />
      ) : null}
      {/* Dialog: nuovo finanziamento */}
      {openNewFin ? (
        <FinanziamentoDialog
          righe={tabellaImporti.filter((r) => r.attivo)}
          onClose={() => setOpenNewFin(false)}
          onSubmit={async (rigaId) => {
            await creaModulo({ tipo: "finanziamento", finanziamento_riga_id: rigaId });
            setOpenNewFin(false);
          }}
        />
      ) : null}
      {/* Dialog: nuovo blocca-prezzo */}
      {openBlocca ? (
        <BloccaPrezzoDialog
          totaleIndicativo={totaleIndicativo}
          onClose={() => setOpenBlocca(false)}
          onSubmit={async (payload) => {
            await creaModulo({ tipo: "blocca_prezzo", contenuto: payload });
            setOpenBlocca(false);
          }}
        />
      ) : null}
    </div>
  );
}

function ModuloSezione({ titolo, descrizione, children }) {
  return (
    <div className="rounded-md border">
      <div className="border-b bg-muted px-3 py-2">
        <p className="text-sm font-semibold text-foreground">{titolo}</p>
        {descrizione ? <p className="text-xs text-muted-foreground mt-0.5">{descrizione}</p> : null}
      </div>
      <div className="p-3 space-y-2">{children}</div>
    </div>
  );
}

function BonificoRiga({ modulo, readOnly, onPatch, onDelete }) {
  const c = modulo.contenuto || {};
  const [tempi, setTempi] = useState(c.tempi || 2);
  const [percentuali, setPercentuali] = useState(c.percentuali || (tempi === 3 ? [30, 40, 30] : [30, 70]));
  const [attivo, setAttivo] = useState(modulo.attivo);

  const cambiaTempi = (n) => {
    setTempi(n);
    const nuova = n === 3 ? [30, 40, 30] : [30, 70];
    setPercentuali(nuova);
    onPatch({ contenuto: { tempi: n, percentuali: nuova } });
  };
  const cambiaPercentuale = (i, v) => {
    const next = [...percentuali];
    next[i] = Number(v) || 0;
    setPercentuali(next);
    onPatch({ contenuto: { tempi, percentuali: next } });
  };

  const somma = percentuali.reduce((a, b) => a + Number(b || 0), 0);
  return (
    <div className="rounded border bg-muted p-2" data-testid={`bonifico-${modulo.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs text-muted-foreground">Tempi</Label>
        <Select value={String(tempi)} onValueChange={(v) => cambiaTempi(Number(v))} disabled={readOnly}>
          <SelectTrigger className="h-8 w-24" data-testid={`bonifico-tempi-${modulo.id}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="2">2 tempi</SelectItem>
            <SelectItem value="3">3 tempi</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {!readOnly ? (
          <>
            <Switch checked={attivo} onCheckedChange={(v) => { setAttivo(v); onPatch({ attivo: v }); }} />
            <button onClick={onDelete} className="text-muted-foreground hover:text-danger-foreground" data-testid={`bonifico-del-${modulo.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        ) : null}
      </div>
      <div className="flex items-center gap-2 mt-2">
        {percentuali.map((p, i) => (
          <div key={i} className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Rata {i + 1}</span>
            <Input
              type="number" min={0} max={100} value={p}
              onChange={(e) => cambiaPercentuale(i, e.target.value)}
              disabled={readOnly}
              className="h-8 w-16 text-right"
              data-testid={`bonifico-perc-${modulo.id}-${i}`}
            />
            <span className="text-xs text-muted-foreground">%</span>
          </div>
        ))}
        <span className={`text-xs ml-2 ${somma === 100 ? "text-success-foreground" : "text-danger-foreground"}`}>
          totale {somma}%
        </span>
      </div>
    </div>
  );
}

function FinanziamentoRiga({ modulo, readOnly, onDelete }) {
  const r = modulo.finanziamento_riga;
  return (
    <div className="rounded border bg-muted p-2 flex items-center gap-3 text-sm"
         data-testid={`finanziamento-${modulo.id}`}>
      <div className="flex-1">
        <p className="font-medium text-foreground">
          {r ? `${eur(r.importo_finanziato)} in ${r.durata_mesi} mesi` : "Riga finanziamento"}
        </p>
        <p className="text-xs text-muted-foreground">
          {r ? (
            <>Rata {eur(r.rata_mensile)}/mese · TAEG <strong>{r.taeg}%</strong>
              {r.ente ? ` · ${r.ente}` : ""}
            </>
          ) : "—"}
        </p>
      </div>
      {!readOnly ? (
        <button onClick={onDelete} className="text-muted-foreground hover:text-danger-foreground" data-testid={`finanziamento-del-${modulo.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

/* Blocca-prezzo: l'acconto si fissa in percentuale OPPURE in euro (call del
   23-09). Al cliente si mostra sempre l'importo in euro: in percentuale lo
   calcola il documento sul totale del preventivo. Qui se ne mostra una stima
   sul totale più alto, perché il venditore sappia che cifra leggerà il cliente. */
function contenutoBlocca(c) {
  const modalita = c?.modalita === "euro" ? "euro" : "percentuale";
  return {
    modalita,
    percentuale: c?.percentuale ?? 10,
    importo: c?.importo_euro != null ? String(c.importo_euro).replace(".", ",") : "",
    giorni: c?.durata_giorni ?? 30,
  };
}

function payloadBlocca(stato) {
  const giorni = Number(stato.giorni);
  if (stato.modalita === "euro") {
    return { modalita: "euro", importo_euro: leggiImporto(stato.importo), durata_giorni: giorni };
  }
  return { modalita: "percentuale", percentuale: Number(stato.percentuale), durata_giorni: giorni };
}

function SceltaModalita({ valore, onChange, disabled, idBase }) {
  const voci = [
    { v: "percentuale", l: "In percentuale" },
    { v: "euro", l: "In euro" },
  ];
  return (
    <div className="inline-flex rounded-md border p-micro gap-micro" role="group" aria-label="Come si fissa l'acconto">
      {voci.map((o) => (
        <button
          key={o.v}
          type="button"
          disabled={disabled}
          aria-pressed={valore === o.v}
          onClick={() => onChange(o.v)}
          data-testid={`${idBase}-modalita-${o.v}`}
          className={[
            "rounded-sm px-3 py-1.5 text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            valore === o.v
              ? "bg-brand-subtle text-brand-subtle-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-foreground",
          ].join(" ")}
        >
          {o.l}
        </button>
      ))}
    </div>
  );
}

function StimaAcconto({ stato, totaleIndicativo }) {
  if (stato.modalita !== "percentuale") return null;
  const p = Number(stato.percentuale);
  if (!totaleIndicativo || !Number.isFinite(p) || p <= 0) return null;
  return (
    <p className="text-xs text-muted-foreground" data-testid="blocca-stima">
      Il cliente legge un acconto di circa{" "}
      <span className="font-semibold tabular-nums text-foreground">{eur((totaleIndicativo * p) / 100)}</span>{" "}
      sul totale di {eur(totaleIndicativo)}.
    </p>
  );
}

function BloccaPrezzoRiga({ modulo, readOnly, onPatch, onDelete, totaleIndicativo }) {
  const [stato, setStato] = useState(() => contenutoBlocca(modulo.contenuto));
  const salva = (prossimo = stato) => onPatch({ contenuto: payloadBlocca(prossimo) });
  const cambia = (campo, valore) => setStato((s) => ({ ...s, [campo]: valore }));
  const cambiaModalita = (m) => {
    const prossimo = { ...stato, modalita: m };
    setStato(prossimo);
    // Passando a «euro» senza importo non si salva ancora: si aspetta la cifra.
    if (m === "percentuale" || leggiImporto(prossimo.importo)) salva(prossimo);
  };
  return (
    <div className="rounded border bg-muted p-group space-y-inside" data-testid={`blocca-prezzo-${modulo.id}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <SceltaModalita valore={stato.modalita} onChange={cambiaModalita} disabled={readOnly} idBase={`blocca-${modulo.id}`} />
        {stato.modalita === "percentuale" ? (
          <label className="flex items-center gap-inside text-xs text-muted-foreground">
            Acconto
            <Input type="number" min={1} max={100} value={stato.percentuale}
                   onChange={(e) => cambia("percentuale", e.target.value)}
                   onBlur={() => salva()} disabled={readOnly} className="h-8 w-16 text-right"
                   data-testid={`blocca-perc-${modulo.id}`} />
            %
          </label>
        ) : (
          <label className="flex items-center gap-inside text-xs text-muted-foreground">
            Acconto
            <Input inputMode="decimal" value={stato.importo} placeholder="500,00"
                   onChange={(e) => cambia("importo", e.target.value)}
                   onBlur={() => leggiImporto(stato.importo) && salva()} disabled={readOnly}
                   className="h-8 w-28 text-right" data-testid={`blocca-euro-${modulo.id}`} />
            €
          </label>
        )}
        <label className="flex items-center gap-inside text-xs text-muted-foreground">
          per
          <Input type="number" min={1} value={stato.giorni}
                 onChange={(e) => cambia("giorni", e.target.value)}
                 onBlur={() => salva()} disabled={readOnly} className="h-8 w-20 text-right"
                 data-testid={`blocca-giorni-${modulo.id}`} />
          giorni
        </label>
        <div className="flex-1" />
        {!readOnly ? (
          <Button type="button" size="sm" variant="ghost" onClick={onDelete} aria-label="Togli il blocca-prezzo">
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      <StimaAcconto stato={stato} totaleIndicativo={totaleIndicativo} />
    </div>
  );
}

function DialogFrame({ title, onClose, children, footer, descrizione }) {
  // La descrizione c'era gia' a schermo, ma non era LEGATA al dialogo: chi
  // ascolta sentiva il titolo e poi il vuoto. `aria-describedby` la aggancia.
  const idDescrizione = useId();
  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 grid place-items-center p-4">
      <Card
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-describedby={idDescrizione}
        className="w-full max-w-lg p-card space-y-4"
      >
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <p id={idDescrizione} className="text-sm text-muted-foreground">
          {descrizione || "Compila i campi e conferma: il modulo viene aggiunto al documento."}
        </p>
        <div className="space-y-3">{children}</div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          {footer}
        </div>
      </Card>
    </div>
  );
}

function BonificoDialog({ onClose, onSubmit }) {
  const [tempi, setTempi] = useState(2);
  const percentuali = tempi === 3 ? [30, 40, 30] : [30, 70];
  return (
    <DialogFrame
      title="Nuovo bonifico"
      onClose={onClose}
      footer={
        <Button onClick={() => onSubmit({ tempi, percentuali })}
                data-testid="dialog-bonifico-submit">
          Aggiungi
        </Button>
      }
    >
      <div className="space-y-2">
        <Label className="text-xs">Tempi</Label>
        <Select value={String(tempi)} onValueChange={(v) => setTempi(Number(v))}>
          <SelectTrigger data-testid="dialog-bonifico-tempi"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="2">2 tempi (30% + 70%)</SelectItem>
            <SelectItem value="3">3 tempi (30% + 40% + 30%)</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">Le percentuali si possono affinare dopo la creazione.</p>
      </div>
    </DialogFrame>
  );
}

function FinanziamentoDialog({ righe, onClose, onSubmit }) {
  const [scelto, setScelto] = useState(righe[0]?.id || "");
  return (
    <DialogFrame
      title="Nuovo finanziamento"
      onClose={onClose}
      footer={
        <Button disabled={!scelto}
                onClick={() => onSubmit(scelto)}
                data-testid="dialog-finanziamento-submit">
          Aggiungi
        </Button>
      }
    >
      <p className="text-xs text-muted-foreground">
        Le rate provengono dalla tabella caricata in impostazioni. Nessuna rata si calcola qui.
      </p>
      <div className="rounded-md border divide-y divide-border max-h-60 overflow-auto">
        {righe.map((r) => (
          <label
            key={r.id}
            className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer ${
              scelto === r.id ? "bg-muted" : ""
            }`}
            data-testid={`fin-row-${r.id}`}
          >
            <input type="radio" name="fin" checked={scelto === r.id} onChange={() => setScelto(r.id)} />
            <div className="flex-1">
              <p className="font-medium text-foreground">
                {eur(r.importo_finanziato)} in {r.durata_mesi} mesi
              </p>
              <p className="text-xs text-muted-foreground">
                Rata {eur(r.rata_mensile)}/mese · TAEG {r.taeg}%{r.ente ? ` · ${r.ente}` : ""}
              </p>
            </div>
          </label>
        ))}
      </div>
    </DialogFrame>
  );
}

function BloccaPrezzoDialog({ onClose, onSubmit, totaleIndicativo }) {
  const [stato, setStato] = useState(() => contenutoBlocca({}));
  const [errore, setErrore] = useState(null);
  const cambia = (campo, valore) => setStato((s) => ({ ...s, [campo]: valore }));
  const conferma = () => {
    const p = payloadBlocca(stato);
    if (stato.modalita === "euro" && !p.importo_euro) {
      setErrore("Scrivi l'acconto in euro, per esempio 500,00.");
      return;
    }
    if (stato.modalita === "percentuale" && !(p.percentuale > 0 && p.percentuale <= 100)) {
      setErrore("La percentuale va da 1 a 100.");
      return;
    }
    if (!(p.durata_giorni >= 1)) {
      setErrore("Il prezzo resta fermo per almeno un giorno.");
      return;
    }
    setErrore(null);
    onSubmit(p);
  };
  return (
    <DialogFrame
      title="Blocca il prezzo"
      descrizione="Un acconto che tiene fermo il prezzo per qualche giorno. Nel documento il cliente legge sempre la cifra in euro."
      onClose={onClose}
      footer={
        <Button onClick={conferma} data-testid="dialog-blocca-submit">
          Aggiungi
        </Button>
      }
    >
      <SceltaModalita valore={stato.modalita} onChange={(m) => cambia("modalita", m)} idBase="dialog-blocca" />
      <div className="grid grid-cols-2 gap-3">
        {stato.modalita === "percentuale" ? (
          <div className="space-y-inside">
            <Label className="text-xs" htmlFor="dialog-blocca-perc">Acconto (%)</Label>
            <Input id="dialog-blocca-perc" type="number" min={1} max={100} value={stato.percentuale}
                   onChange={(e) => cambia("percentuale", e.target.value)} data-testid="dialog-blocca-perc" />
          </div>
        ) : (
          <div className="space-y-inside">
            <Label className="text-xs" htmlFor="dialog-blocca-euro">Acconto (€)</Label>
            <Input id="dialog-blocca-euro" inputMode="decimal" placeholder="500,00" value={stato.importo}
                   onChange={(e) => cambia("importo", e.target.value)} data-testid="dialog-blocca-euro" />
          </div>
        )}
        <div className="space-y-inside">
          <Label className="text-xs" htmlFor="dialog-blocca-gg">Per quanti giorni</Label>
          <Input id="dialog-blocca-gg" type="number" min={1} value={stato.giorni}
                 onChange={(e) => cambia("giorni", e.target.value)} data-testid="dialog-blocca-gg" />
        </div>
      </div>
      <StimaAcconto stato={stato} totaleIndicativo={totaleIndicativo} />
      {errore ? <p className="text-xs text-danger-foreground" role="alert">{errore}</p> : null}
    </DialogFrame>
  );
}

/* ================================================================= */
/* BLOCCO 16 — Recensione                                             */
/* ================================================================= */
export function RecensioneBlock({ preventivoId, readOnly }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  // Qui il tipo di fornitura non c'entra: la recensione e' legata al
  // preventivo, e `preventivoId` e' davvero l'unica dipendenza.
  const load = useCallback(async () => {
    try {
      const r = await api.get(`/preventivatore/${preventivoId}/recensione`);
      setData(r.data);
    } catch (e) { toast.error(messaggioErrore(e)); }
  }, [preventivoId]);
  useEffect(() => { load(); }, [load]);

  const rigenera = async () => {
    setBusy(true);
    try {
      const r = await registraSalvataggio(api.post(`/preventivatore/${preventivoId}/recensione/rigenera`, {}));
      if (!r.data.recensione) toast.info(r.data.motivo || "Nessuna recensione disponibile");
      else toast.success("Recensione scelta");
      await load();
    } catch (e) { toast.error(messaggioErrore(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <p className="text-xs text-muted-foreground">Caricamento…</p>;
  const rec = data.recensione;
  const imp = data.impostazioni || {};

  return (
    <div className="space-y-3" data-testid="recensione-block">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-muted-foreground">
          Recensione verbatim dalla libreria — mai riscritta, mai in terza persona. Il numero e la media
          si leggono dalle impostazioni.
        </p>
        {!readOnly ? (
          <Button size="sm" variant="outline" onClick={rigenera} disabled={busy} data-testid="recensione-rigenera">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Scegli un'altra
          </Button>
        ) : null}
      </div>

      {rec ? (
        <blockquote
          className="rounded-md border bg-muted p-group"
          data-testid="recensione-testo"
        >
          <Quote className="h-4 w-4 text-muted-foreground mb-1" />
          <p className="text-sm text-foreground whitespace-pre-wrap">{rec.testo}</p>
          <p className="text-xs text-muted-foreground mt-2">
            — <strong>{rec.nome_autore}</strong>
            {rec.data_recensione ? ` · ${rec.data_recensione}` : ""}
            {rec.fonte ? ` · ${rec.fonte === "google" ? "Google" : rec.fonte}` : ""}
          </p>
        </blockquote>
      ) : (
        <p className="text-xs text-muted-foreground italic">Nessuna recensione scelta per questo preventivo.</p>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <ExternalLink className="h-3 w-3" />
        Dati profilo Google: <strong>{imp.numero_totale || "—"}</strong> recensioni,
        media <strong>{imp.media || "—"}</strong>.
      </p>
    </div>
  );
}
